import type { FunctionComponent, ReactNode, SVGProps } from "react";
import { cn } from "@/utils/helpers";
import PlatformGlyph, { platformLabel } from "../PlatformGlyph";

type Icon = FunctionComponent<SVGProps<SVGSVGElement>>;

/*
 * Tags (CreativeOS Library.lib.pen, Components / Status and feedback, style B).
 * Read-only, radius 6, tonal fills with no hairline. Every status pairs a
 * written label with its own icon: colour is never the only cue. Uppercase is
 * applied here, to mono labels only.
 *
 * Tags by job (Workshop): what a thing is (kind), its state, a run that is live
 * now, what waits on you (queued) and where it is scheduled. These are the
 * 20px outlined tags; everything else is the 24px tonal tag.
 */

const LABEL = "font-mono text-[11px] uppercase leading-none tracking-[0.12em]";

/** Tag / Default and Tag / With glyph: a tonal tag with an optional 12px glyph. */
export function KitTag({
  children,
  icon: Glyph,
  tone = "muted",
  className,
}: {
  children: ReactNode;
  icon?: Icon;
  /** muted (Default), ink (With glyph) or client (a client's name, accent ink). */
  tone?: "muted" | "ink" | "client";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-6 shrink-0 items-center gap-1.5 rounded-tag bg-co-tonal px-2",
        LABEL,
        tone === "muted" ? "text-co-muted" : tone === "client" ? "text-co-accent-ink" : "text-co-text2",
        className,
      )}
    >
      {Glyph ? <Glyph aria-hidden className="size-3 shrink-0" /> : null}
      {children}
    </span>
  );
}

/** Tag / Pillar: the pillar's 2px mark on the Purple to Blue scale; the name stays neutral. */
export function KitPillarTag({ name, color, className }: { name: string; color: string; className?: string }) {
  return (
    <span className={cn("inline-flex h-6 shrink-0 items-center gap-2 rounded-tag bg-co-tonal px-2 text-co-text2", LABEL, className)}>
      <span aria-hidden className="h-3 w-0.5 shrink-0" style={{ background: color }} />
      {name}
    </span>
  );
}

/** Tag / Type dot: a 6px square in the type's colour and its word, no fill. */
export function KitTypeDot({ label, color, className }: { label: string; color: string; className?: string }) {
  return (
    <span className={cn("inline-flex h-6 shrink-0 items-center gap-2 text-co-muted", LABEL, className)}>
      <span aria-hidden className="size-1.5 shrink-0" style={{ background: color }} />
      {label}
    </span>
  );
}

/** Tag / Platform: the platform's own mark in a 24px tonal square. Never a lucide icon. */
export function KitPlatformTag({ platform, className }: { platform: string; className?: string }) {
  return (
    <span className={cn("inline-grid size-6 shrink-0 place-items-center rounded-tag bg-co-tonal text-co-muted", className)}>
      <PlatformGlyph platform={platform} className="size-3.5" />
      <span className="sr-only">{platformLabel(platform)}</span>
    </span>
  );
}

export type KitJobTagJob = "kind" | "state" | "live" | "queued" | "scheduled";

const JOB_CLASSES: Record<KitJobTagJob, string> = {
  kind: "border-co-accent text-co-accent-ink",
  state: "border-co-line2 text-co-muted",
  live: "border-co-live text-co-live",
  queued: "border-co-accent-border text-co-accent-ink",
  scheduled: "border-[var(--co-state-scheduled)] text-[var(--co-state-scheduled)]",
};

/**
 * Tags by job: Tag / Kind (what a thing is), State (neutral mono), Live run
 * (green, only a run that is live now), Queued (what waits on you) and
 * Scheduled (where it sits on the calendar). 20px, outlined, mono 10.
 */
export function KitJobTag({
  job,
  icon: Glyph,
  children,
  className,
}: {
  job: KitJobTagJob;
  /** Kind only: a 12px leading glyph (film for a video, sparkles for a generated output). */
  icon?: Icon;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 items-center gap-1 rounded-tag border px-1.5 font-mono text-[10px] uppercase leading-none tracking-[0.09em]",
        JOB_CLASSES[job],
        className,
      )}
    >
      {Glyph ? <Glyph aria-hidden className="size-3 shrink-0 text-co-muted" /> : null}
      {children}
    </span>
  );
}

/**
 * Origin line: where a thing came from, an icon and a short accent-ink label
 * ("From the field report of 20 Aug"). Quiet is the muted byline ("Made by").
 */
export function KitOriginLine({
  icon: Glyph,
  children,
  quiet = false,
  className,
}: {
  icon?: Icon;
  children: ReactNode;
  quiet?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center",
        quiet ? "gap-1.5 text-[13px] text-co-muted" : "gap-[5px] text-[12.5px] font-semibold text-co-accent-ink",
        className,
      )}
    >
      {Glyph ? <Glyph aria-hidden className="size-[13px] shrink-0" /> : null}
      {children}
    </span>
  );
}

/**
 * Inline note · Attention: a radius 12 note with an attention edge and a
 * triangle mark, for a limit the person is about to pass. It sits in the flow;
 * it is not a banner.
 */
export function KitInlineNote({ icon: Glyph, children, className }: { icon: Icon; children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex items-start gap-2.5 rounded-card border border-co-attention px-3.5 py-3", className)}>
      <Glyph aria-hidden className="mt-0.5 size-[15px] shrink-0 text-co-attention" />
      <p className="text-[13px] leading-[1.5] text-co-text2">{children}</p>
    </div>
  );
}
